import React, { useCallback, useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, withCitrusTheme } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import {
  FlexPortionTileBadge,
  FlexPortionTileEditor,
  type FlexPortionTrackingContext,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '@features/recipe-tile'
import type {
  CampaignBannerRecipeItem,
  CompactCampaignBannerRecipeItem,
} from '@library/api-menu-experience'
import { DeviceType, useDeviceType } from '@library/dom'
import { CampaignBanner, CompactCampaignBanner } from '@library/menu-page'
import type { CollectionUUID, MenuRecipeTileRequestParams, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useCollections, useCurrentCollectionId, useSelectCollection } from 'hooks/menu-collections'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import type { SelectCollectionEvent } from 'routes/Menu/MenuExperience/MenuPage/types'
import { useMerchandisingBannerBasket } from 'routes/Menu/MenuExperience/hooks/useMerchandisingBannerBasket'
import type {
  CampaignBannerHeaderContentBlock,
  CompactCampaignBannerHeaderContentBlock,
} from 'routes/Menu/MenuRecipesPage/CollectionHeader'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { useRecipeTagsCwcWeb } from 'routes/Menu/hooks/useRecipeTagsCwcWeb'
import { useRecipeTileRefresh } from 'routes/Menu/hooks/useRecipeTileRefresh'
import { getBasketDate, getBasketMenuUuid } from 'selectors/basket'

export type FeaturedRangeCampaignBannerProps = {
  merchandisingHeader: CampaignBannerHeaderContentBlock | CompactCampaignBannerHeaderContentBlock
}

type MerchandisingRecipeItem = CampaignBannerRecipeItem | CompactCampaignBannerRecipeItem

const isRecipeItem = (
  item: CampaignBannerHeaderContentBlock['items'][number] | CompactCampaignBannerRecipeItem,
): item is MerchandisingRecipeItem => item.type === 'recipe'

const CampaignHeaderBox = withCitrusTheme(Box, () => ({
  [`@media (max-width: 543px)`]: {
    marginTop: '-1rem',
  },
  [`@media (min-width: 544px)`]: {
    marginLeft: 12,
    marginRight: 12,
  },
  [`@media (min-width: 768px)`]: {
    marginLeft: -30,
    marginRight: -30,
  },
}))

export const FeaturedRangeCampaignBanner = ({
  merchandisingHeader,
}: FeaturedRangeCampaignBannerProps) => {
  const dispatch = useDispatch()
  const { menuId } = useMenuMeta()
  const basketDate = useSelector(getBasketDate)
  const basketMenuUuid = useSelector(getBasketMenuUuid)
  const deviceType = useDeviceType()
  const isMobile = deviceType === DeviceType.MOBILE
  const isRecipeTagsCwcEnabled = useRecipeTagsCwcWeb()
  const currentCollectionId = useCurrentCollectionId()
  const selectCollection = useSelectCollection()
  const collections = useCollections()
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { recipeCount, removeRecipeByUuid, updateRecipePortionsByUuid } = useBasket()
  const { refreshRecipeTile } = useRecipeTileRefresh({
    deliveryDate: basketDate,
    menuId: basketMenuUuid,
  })
  const { canAddRecipes, getRecipeCardState, onAddClick, onRemoveClick, reachedLimit } =
    useMerchandisingBannerBasket(merchandisingHeader)
  const [flexEditorRecipeId, setFlexEditorRecipeId] = useState<RecipeUUID | null>(null)

  const recipeById = useMemo(() => {
    const items = merchandisingHeader.items as Array<
      CampaignBannerHeaderContentBlock['items'][number] | CompactCampaignBannerRecipeItem
    >

    return Object.fromEntries(
      items.filter(isRecipeItem).map((recipe) => [recipe.id, recipe]),
    ) as Partial<Record<RecipeUUID, MerchandisingRecipeItem>>
  }, [merchandisingHeader.items])

  const collectionSlugById = useMemo(
    () =>
      Object.fromEntries(
        collections.map((collection) => [collection.id, collection.slug]),
      ) as Record<string, string>,
    [collections],
  )
  const currentCollectionSlug = collectionSlugById[currentCollectionId]

  const onRecipeClick = useCallback(
    (recipeId: RecipeUUID, openFlexPortionEditor = false) => {
      dispatch(
        showDetailRecipe(
          recipeId,
          {
            source: 'merch_banner',
            merch_header_type: merchandisingHeader.type,
          },
          { openFlexPortionEditor },
        ),
      )
      datadogRum?.addAction('open_menu_recipe_detail', {
        menu_id: menuId,
        recipe_id: recipeId,
      })
    },
    [dispatch, menuId, merchandisingHeader.type],
  )

  const getFlexTrackingContext = useCallback(
    (recipeId: RecipeUUID): FlexPortionTrackingContext => ({
      basketRecipeCount: recipeCount,
      menuUuid: menuId,
      recipePortionCount: getRecipeCardState(recipeId).numPortions ?? 0,
      recipeUuid: recipeId,
      source: 'view_recipe_list_screen',
    }),
    [getRecipeCardState, menuId, recipeCount],
  )

  const handleEditFlexPortions = useCallback(
    (recipeId: RecipeUUID) => {
      trackClickEditRecipeQuantity(getFlexTrackingContext(recipeId))

      if (isMobile) {
        setFlexEditorRecipeId(recipeId)

        return
      }

      onRecipeClick(recipeId, true)
    },
    [getFlexTrackingContext, isMobile, onRecipeClick],
  )

  const handleUpdateRecipePortions = useCallback(
    (recipeId: RecipeUUID, numPortions: MenuRecipeTileRequestParams['numPortions']) => {
      updateRecipePortionsByUuid(recipeId, numPortions)
      void refreshRecipeTile(recipeId, numPortions)
      trackUpdatePortion(getFlexTrackingContext(recipeId), numPortions)
    },
    [getFlexTrackingContext, refreshRecipeTile, updateRecipePortionsByUuid],
  )

  const handleRemoveRecipe = useCallback(
    (recipeId: RecipeUUID) => {
      removeRecipeByUuid(recipeId, 'grid', 'collection_merchandising_header')
      setFlexEditorRecipeId(null)
    },
    [removeRecipeByUuid],
  )

  const renderFlexPortionBadge = useCallback(
    (recipeId: RecipeUUID) => {
      const { isInBasket, quantity = 0, numPortions = 0 } = getRecipeCardState(recipeId)

      if (!isInBasket || quantity <= 0) {
        return null
      }

      return (
        <FlexPortionTileBadge
          recipeCount={quantity}
          portionCount={numPortions}
          onEdit={() => handleEditFlexPortions(recipeId)}
        />
      )
    },
    [getRecipeCardState, handleEditFlexPortions],
  )

  const renderFlexPortionEditor = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]
      const { isInBasket, quantity = 0, numPortions = 0 } = getRecipeCardState(recipeId)

      if (!isMobile || flexEditorRecipeId !== recipeId || !recipe || !isInBasket || quantity <= 0) {
        return null
      }

      return (
        <FlexPortionTileEditor
          isOpen
          recipeCount={quantity}
          recipeId={recipeId}
          numPortions={numPortions}
          portions={recipe.portions}
          onClose={() => setFlexEditorRecipeId(null)}
          onRemoveRecipe={handleRemoveRecipe}
          onUpdateRecipePortions={handleUpdateRecipePortions}
          onSelectPortions={(updatedNumPortions: number) =>
            trackSelectPortionCount(getFlexTrackingContext(recipeId), updatedNumPortions)
          }
        />
      )
    },
    [
      flexEditorRecipeId,
      getFlexTrackingContext,
      getRecipeCardState,
      handleRemoveRecipe,
      handleUpdateRecipePortions,
      isMobile,
      recipeById,
    ],
  )

  const onCtaClick = useCallback(
    (targetCategory: { id: string; slug: string }) => {
      const collectionId = targetCategory.id
      const collectionSlug = collectionSlugById[collectionId]

      if (collectionSlug) {
        queueTrackingEvent<SelectCollectionEvent>({
          action: 'select_collection',
          properties: {
            collection_id: collectionId as CollectionUUID,
            collection_slug:
              collectionSlug as SelectCollectionEvent['properties']['collection_slug'],
            current_collection_id:
              currentCollectionId as SelectCollectionEvent['properties']['current_collection_id'],
            ...(currentCollectionSlug && {
              current_collection_slug:
                currentCollectionSlug as SelectCollectionEvent['properties']['current_collection_slug'],
            }),
            merch_header_type: merchandisingHeader.type,
            navigation_method: 'merch_banner',
          },
        })
      }

      selectCollection(collectionId as CollectionUUID)
    },
    [
      collectionSlugById,
      currentCollectionSlug,
      currentCollectionId,
      merchandisingHeader.type,
      selectCollection,
    ],
  )

  return (
    <CampaignHeaderBox data-testid="featured-range-header-box">
      {(() => {
        switch (merchandisingHeader.type) {
          case 'campaign-banner':
            return (
              <CampaignBanner
                contentBlock={merchandisingHeader}
                getRecipeCardState={getRecipeCardState}
                canAddRecipes={canAddRecipes}
                reachedLimit={reachedLimit}
                onAddClick={onAddClick}
                onBlockedAddAttempt={setBrowserCTAVisible}
                onCtaClick={onCtaClick}
                onRecipeClick={onRecipeClick}
                onRemoveClick={onRemoveClick}
                renderFlexPortionBadge={renderFlexPortionBadge}
                renderFlexPortionEditor={renderFlexPortionEditor}
                isRecipeTagsCwcEnabled={isRecipeTagsCwcEnabled}
              />
            )
          case 'compact-campaign-banner':
            return (
              <CompactCampaignBanner
                contentBlock={merchandisingHeader}
                getRecipeCardState={getRecipeCardState}
                canAddRecipes={canAddRecipes}
                reachedLimit={reachedLimit}
                onAddClick={onAddClick}
                onBlockedAddAttempt={setBrowserCTAVisible}
                onCtaClick={onCtaClick}
                onRecipeClick={onRecipeClick}
                onRemoveClick={onRemoveClick}
                renderFlexPortionBadge={renderFlexPortionBadge}
                renderFlexPortionEditor={renderFlexPortionEditor}
              />
            )
          default:
            return null
        }
      })()}
    </CampaignHeaderBox>
  )
}
